import {
    type BarDuration,
    type BarOHLCV,
    type EntityType,
    type MicrotrendComparisonConfig,
    type MicrotrendComparisonSnapshot,
    type MicrotrendComparisonResult,
    type MicrotrendPersistedResearchImpulse,
    type MicrotrendRustLeg,
    MICROTREND_COMPARISON_DURATION_SECONDS
} from '@einhaus/project314-types';
import { getDateFromUnix, getDateFromUtc } from '@einhaus/meteoric-helpers';

/** Select whole available chart days, so weekends do not turn a short equity window into no data. */
export function recentMicrotrendComparisonWindow(bars: readonly BarOHLCV[], entityType: EntityType, duration?: BarDuration) {
    const last = bars.at(-1);
    if (!last) return null;
    const endDate = getDateFromUnix(last.t, 'ymd');
    let startDate = endDate;
    const days = new Set<string>();
    const maximumDays =
        duration === '1d'
            ? 400
            : duration && ['60m', '120m', '180m', '240m'].includes(duration)
              ? 60
              : entityType === 'Cryptocurrency'
                ? 1
                : 2;
    const maximumSpanSeconds =
        duration === '1d' ? 5 * 366 * 86400 : duration && ['60m', '120m', '180m', '240m'].includes(duration) ? 400 * 86400 : 30 * 86400;
    for (let i = bars.length - 1; i >= 0; i--) {
        const bar = bars[i]!;
        // Very sparse or halted symbols may have their last two observations months apart.
        if (last.t - bar.t > maximumSpanSeconds) break;
        const day = getDateFromUnix(bar.t, 'ymd');
        if (!days.has(day) && days.size === maximumDays) break;
        days.add(day);
        startDate = day;
    }
    return { startDateTime: `${startDate} 00:00:00`, endDateTime: `${endDate} 23:59:59` };
}

/** Display availability, including shortened equity buckets and midnight-dated daily bars. */
export function microtrendComparisonBarClose(snapshot: MicrotrendComparisonSnapshot, timestamp: number) {
    const nominalClose = timestamp + MICROTREND_COMPARISON_DURATION_SECONDS[snapshot.duration];
    if (snapshot.entityType === 'Cryptocurrency') return nominalClose;
    const session =
        snapshot.duration === '1d'
            ? snapshot.sessions.find(
                  (candidate) =>
                      getDateFromUtc(candidate.start * 1000, 'ymd', 'America/New_York') ===
                      getDateFromUtc(timestamp * 1000, 'ymd', 'America/New_York')
              )
            : snapshot.sessions.find((candidate) => timestamp < candidate.end && nominalClose > candidate.start);
    if (!session) throw new Error('Comparison bar has no matching calendar session');
    return snapshot.duration === '1d' ? session.end : Math.min(nominalClose, session.end);
}

/** Gate both chart segments and details before the next effect runs after a rewind/config change. */
export function currentMicrotrendComparisonResult(
    answer: { config: MicrotrendComparisonConfig; persistedRunId: string; result: MicrotrendComparisonResult } | null,
    config: MicrotrendComparisonConfig,
    cutoffCount: number,
    persistedRunId = ''
): MicrotrendComparisonResult | null {
    return answer?.config === config && answer.persistedRunId === persistedRunId && answer.result.cutoffCount === cutoffCount
        ? answer.result
        : null;
}

export type MicrotrendComparisonMode = 'both' | 'rust' | 'typescript';
export interface MicrotrendComparisonRow {
    id: string;
    source: 'Rust' | 'TypeScript' | 'Persisted composite';
    direction: string;
    status: 'confirmed' | 'developing' | 'interrupted' | 'legacy' | 'finalized' | 'provisional';
    points: { time: number; value: number }[];
    color: string;
    rustLeg?: MicrotrendRustLeg;
    persistedImpulse?: MicrotrendPersistedResearchImpulse;
    confirmedAt?: number;
    confirmationSequence?: number;
    delayBars?: number;
    delaySeconds?: number;
    interruption?: string;
    reportedPct?: number | null;
    currentPullback?: number;
    currentRetracement?: number;
    barsSinceExtreme?: number;
    barCount: number;
}

export function microtrendComparisonRows(
    result: MicrotrendComparisonResult | null,
    mode: MicrotrendComparisonMode,
    includePersisted = false
): MicrotrendComparisonRow[] {
    if (!result) return [];
    const rows: MicrotrendComparisonRow[] =
        mode === 'rust'
            ? []
            : result.legacy.map((leg) => ({
                  id: leg.id,
                  source: 'TypeScript',
                  direction: leg.direction,
                  status: 'legacy',
                  points: leg.points,
                  color: leg.direction === 'uptrend' ? '#00ff42' : leg.direction === 'downtrend' ? '#ff00de' : '#f0ff00',
                  reportedPct: leg.reportedPriceChangePct,
                  barCount: leg.includedBarCount
              }));
    const addRust = (leg: MicrotrendRustLeg, status: MicrotrendComparisonRow['status'], extra: Partial<MicrotrendComparisonRow> = {}) => {
        rows.push({
            id: leg.id,
            source: 'Rust',
            direction: leg.direction,
            status,
            points: [
                { time: leg.origin.barOpenedAt, value: leg.origin.price },
                { time: leg.endpoint.barOpenedAt, value: leg.endpoint.price }
            ],
            color: leg.direction === 'up' ? '#38bdf8' : '#fb923c',
            rustLeg: leg,
            barCount: leg.metrics.includedBarCount,
            ...extra
        });
    };
    if (mode !== 'typescript') {
        for (const item of result.rust.confirmed)
            addRust(item.leg, 'confirmed', {
                confirmedAt: item.confirmed.availableAt,
                confirmationSequence: item.confirmed.sequence,
                delayBars: item.confirmationDelayBars,
                delaySeconds: item.confirmationDelaySeconds
            });
        for (const item of result.rust.interrupted)
            addRust(item.developing.leg, 'interrupted', {
                interruption: item.interruption.reason,
                currentPullback: item.developing.currentClosePullback,
                currentRetracement: item.developing.currentRetracementFraction,
                barsSinceExtreme: item.developing.barsSinceExtreme
            });
        const state = result.rust.state;
        if (state.kind === 'developingUp' || state.kind === 'developingDown')
            addRust(state.value.leg, 'developing', {
                currentPullback: state.value.currentClosePullback,
                currentRetracement: state.value.currentRetracementFraction,
                barsSinceExtreme: state.value.barsSinceExtreme
            });
        if (includePersisted)
            for (const impulse of result.persisted?.impulses ?? [])
                rows.push({
                    id: `persisted:${impulse.id}`,
                    source: 'Persisted composite',
                    direction: impulse.direction,
                    status: impulse.lifecycle,
                    points: [
                        { time: impulse.origin.barOpenedAt, value: impulse.origin.price },
                        { time: impulse.endpoint.barOpenedAt, value: impulse.endpoint.price }
                    ],
                    color: impulse.direction === 'up' ? '#a78bfa' : '#f472b6',
                    persistedImpulse: impulse,
                    confirmedAt: impulse.availableAt,
                    barCount: impulse.metrics.barIntervals + 1
                });
    }
    return rows.sort((a, b) => (a.points[0]?.time ?? 0) - (b.points[0]?.time ?? 0));
}
